/**
 * Bottom sheet (spec 3.24): surface fill, 4 px top corners, e2 shadow, a handle, a title and
 * actions at the bottom. Escape or the backdrop closes it; focus moves in and comes back.
 */

import { type ReactNode, useEffect, useId, useRef } from 'react';

export function Sheet({
  title,
  onClose,
  children,
  actions,
  description,
}: {
  title: string;
  onClose: () => void;
  children?: ReactNode;
  actions: ReactNode;
  description?: string;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const panel = useRef<HTMLDivElement>(null);
  // The app re-renders every second (clock); keep the latest onClose without re-running the effect.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = panel.current?.querySelector<HTMLElement>(
      'input, textarea, button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close.current();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      previous?.focus();
    };
  }, []);

  return (
    <div className="sheet-layer">
      <button type="button" className="sheet-backdrop" aria-label="Kapat" tabIndex={-1} onClick={onClose} />
      <div
        ref={panel}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
      >
        <span className="sheet__handle" aria-hidden="true" />
        <h2 id={titleId} className="sheet__title">
          {title}
        </h2>
        {description ? (
          <p id={descriptionId} className="sheet__description">
            {description}
          </p>
        ) : null}
        {children ? <div className="sheet__body">{children}</div> : null}
        <div className="sheet__actions">{actions}</div>
      </div>
    </div>
  );
}

/** A row of single-choice chips (reasons, defer options), built on native radio buttons. */
export function ChoiceChips<T extends string>({
  label,
  options,
  value,
  onChange,
  labels,
}: {
  label: string;
  options: readonly T[];
  value: T | null;
  onChange: (value: T) => void;
  /** Shown text per option when the value is a code (reason codes on the wire, Turkish on screen). */
  labels?: Readonly<Record<T, string>>;
}) {
  const name = useId();
  return (
    <fieldset className="chips">
      <legend className="sr-only">{label}</legend>
      {options.map((option) => (
        <label key={option} className={value === option ? 'chip chip--on' : 'chip'}>
          <input
            type="radio"
            className="chip__input"
            name={name}
            value={option}
            checked={value === option}
            onChange={() => onChange(option)}
          />
          {labels ? labels[option] : option}
        </label>
      ))}
    </fieldset>
  );
}
